import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { convertToSnakeCase } from '@/utils/string';
import { getParamsFromNameValue } from '@/utils/salt-item';
import { ItemContext_SaltItemFieldsFragment, NutrientType } from '@/types';
import { NutritionalInfoNutrient } from '@/contexts/item-context-types';
import { NutritionLabel } from '@/components/nutrition-label';
import { RichInfoSection } from './rich-info-section';

interface Props {
  horizontal?: boolean;
  saltItemInfo: ItemContext_SaltItemFieldsFragment;
}

/* eslint-disable typescript-sort-keys/string-enum */
enum NutrientCode {
  AS = 'added_sugars',
  CA = 'calcium',
  CALORIES = 'calories',
  CHOLE = 'cholesterol',
  FIBTG = 'dietary_fiber',
  FE = 'iron',
  K = 'potassium',
  PROCNT = 'protein',
  FASAT = 'saturated_fat',
  NA = 'sodium',
  CHOCDF = 'total_carbohydrate',
  FAT = 'total_fat',
  SUGAR = 'total_sugars',
  FATRN = 'trans_fat',
  VITA = 'vitamin_a',
  VITC = 'vitamin_c',
  VITD = 'vitamin_d',
  TOCPHA = 'vitamin_e',
}
/* eslint-enable typescript-sort-keys/string-enum */

const NUTRITIONAL_INFO_NUTRIENT_DEFAULT = {
  amount: '',
  percent_dv: null,
};

export const RichInfoSaltNutrients: React.FC<Props> = ({
  saltItemInfo,
  horizontal,
}) => {
  const { packagingAndStorage, servingAndPreparation, dietaryInformation } =
    saltItemInfo;

  const dietaryNutrients = useMemo(() => {
    return dietaryInformation.nutrients.reduce(
      (
        acc: { [key: string]: NutritionalInfoNutrient },
        nutrient: NutrientType,
      ) => {
        if (!nutrient.name) {
          return acc;
        }
        // remove hyphen from nutrient name
        const cleanNutrientName = nutrient.name.replace(/-/g, '');
        const nutrientCode =
          NutrientCode[cleanNutrientName as keyof typeof NutrientCode];
        const nutrientName = convertToSnakeCase(nutrient.name);
        const nutrientPercentDv = Number(nutrient.percentDv?.replace('%', ''));

        acc[nutrientCode || nutrientName] = {
          amount: nutrient.amount ?? '',
          percent_dv: !isNaN(nutrientPercentDv) ? nutrientPercentDv : null,
          unit_name: nutrient.unitName?.toLowerCase() ?? '',
        };
        return acc;
      },
      {},
    );
  }, [dietaryInformation]);

  const nutritionalParams = useMemo(() => {
    return {
      ...getParamsFromNameValue(dietaryInformation.additionalInformation),
      ...getParamsFromNameValue(
        packagingAndStorage.packaging.additionalInformation,
      ),
      ...getParamsFromNameValue(servingAndPreparation.serving),
    };
  }, [dietaryInformation, packagingAndStorage, servingAndPreparation]);

  const nutrientFields = [
    dietaryNutrients.added_sugars,
    dietaryNutrients.calcium,
    dietaryNutrients.calories,
    dietaryNutrients.cholesterol,
    dietaryNutrients.dietary_fiber,
    dietaryNutrients.iron,
    dietaryNutrients.potassium,
    dietaryNutrients.protein,
    dietaryNutrients.saturated_fat,
    nutritionalParams['Serving Size'],
    nutritionalParams['Servings Per Container'],
    dietaryNutrients.sodium,
    dietaryNutrients.total_carbohydrate,
    dietaryNutrients.total_fat,
    dietaryNutrients.total_sugars,
    dietaryNutrients.trans_fat,
    dietaryNutrients.vitamin_a,
    dietaryNutrients.vitamin_c,
    dietaryNutrients.vitamin_d,
    dietaryNutrients.vitamin_e,
  ];

  const validFieldCount = nutrientFields.filter((field) => field).length;

  // If fewer than 3 valid properties exist, don't render nutrition info
  if (validFieldCount < 3) {
    return null;
  }

  const nutritionInfo = {
    added_sugars: dietaryNutrients.added_sugars || null,
    calcium: dietaryNutrients.calcium || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    calories: dietaryNutrients.calories?.amount || '',
    cholesterol:
      dietaryNutrients.cholesterol || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    dietary_fiber:
      dietaryNutrients.dietary_fiber || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    iron: dietaryNutrients.iron || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    potassium: dietaryNutrients.potassium || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    protein: dietaryNutrients.protein || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    saturated_fat:
      dietaryNutrients.saturated_fat || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    serving_size: nutritionalParams['Serving Size'] || '',
    servings_per_container: nutritionalParams['Servings Per Container'] || '',
    sodium: dietaryNutrients.sodium || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    total_carbohydrate:
      dietaryNutrients.total_carbohydrate || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    total_fat: dietaryNutrients.total_fat || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    total_sugars:
      dietaryNutrients.total_sugars || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    trans_fat: dietaryNutrients.trans_fat || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    vitamin_a: dietaryNutrients.vitamin_a || null,
    vitamin_c: dietaryNutrients.vitamin_c || null,
    vitamin_d: dietaryNutrients.vitamin_d || NUTRITIONAL_INFO_NUTRIENT_DEFAULT,
    vitamin_e: dietaryNutrients.vitamin_e || null,
  };

  return (
    <RichInfoSection horizontal={horizontal} title={i18n.t('nutrition_facts')}>
      <NutritionLabel info={nutritionInfo} tabular={horizontal} />
    </RichInfoSection>
  );
};
